/**
 * Owner/contact enrichment capture from Lead Supply and lead detail.
 */
import { formatInteger } from "./format.js";
import { labelize } from "./dashboard-html.js";
import { authenticatedFetch, DashboardAuthRequiredError } from "./dashboard-auth.js";
import type { DashboardActionHost } from "./dashboard-referenced-deed-actions.js";

export function bindOwnerContactEnrichmentActions(statusElement: HTMLElement, actions: DashboardActionHost): void {
  document.querySelectorAll<HTMLFormElement>("[data-owner-contact-enrichment-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const leadId = form.dataset.leadId?.trim() || "";
      if (!leadId) {
        statusElement.textContent = "Owner/contact enrichment cancelled; no lead is linked to this task.";
        return;
      }
      const confidenceValue = form.querySelector<HTMLInputElement>("[data-owner-contact-confidence]")?.value.trim() || "";
      if (!confidenceValue) {
        statusElement.textContent = "Owner/contact enrichment cancelled; enter a confidence value between 0 and 1.";
        return;
      }
      const confidence = Number(confidenceValue);
      if (!Number.isFinite(confidence) || confidence < 0 || confidence > 1) {
        statusElement.textContent = "Owner/contact enrichment cancelled; confidence must be between 0 and 1.";
        return;
      }
      const ownerName = form.querySelector<HTMLInputElement>("[data-owner-contact-owner]")?.value.trim() || "";
      const phone = form.querySelector<HTMLInputElement>("[data-owner-contact-phone]")?.value.trim() || "";
      const email = form.querySelector<HTMLInputElement>("[data-owner-contact-email]")?.value.trim() || "";
      const mailingAddress = form.querySelector<HTMLInputElement>("[data-owner-contact-mailing]")?.value.trim() || "";
      const source = form.querySelector<HTMLInputElement>("[data-owner-contact-source]")?.value.trim() || "";
      const completeTaskId = form.dataset.taskId?.trim() || "";
      const payload: {
        ownerName?: string;
        phone?: string;
        email?: string;
        mailingAddress?: string;
        source?: string;
        confidence: number;
        note: string;
        completeTaskId?: string;
      } = {
        confidence,
        note: form.querySelector<HTMLInputElement>("[data-owner-contact-note]")?.value.trim() || "Owner/contact evidence reviewed in dashboard."
      };
      if (ownerName) payload.ownerName = ownerName;
      if (phone) payload.phone = phone;
      if (email) payload.email = email;
      if (mailingAddress) payload.mailingAddress = mailingAddress;
      if (source) payload.source = source;
      if (completeTaskId) payload.completeTaskId = completeTaskId;
      if (!payload.ownerName && !payload.phone && !payload.email && !payload.mailingAddress) {
        statusElement.textContent = "Owner/contact enrichment cancelled; enter an owner name, phone, email, or mailing address.";
        return;
      }
      void submitOwnerContactEnrichment(leadId, payload, statusElement, actions);
    });
  });
}

export async function submitOwnerContactEnrichment(
  leadId: string,
  payload: {
    ownerName?: string;
    phone?: string;
    email?: string;
    mailingAddress?: string;
    source?: string;
    confidence: number;
    note: string;
    completeTaskId?: string;
  },
  statusElement: HTMLElement,
  actions: DashboardActionHost
): Promise<void> {
  statusElement.textContent = `${leadId}: recording owner/contact evidence...`;
  try {
    const response = await authenticatedFetch(`/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/owner-contact`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload)
    });
    const body = (await response.json()) as {
      error?: string;
      contactMethodsUpserted?: number;
      contactReadiness?: string;
      status?: string;
    };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${leadId}: recorded owner/contact evidence; ${formatInteger(body.contactMethodsUpserted ?? 0)} contact method(s); readiness ${labelize(body.contactReadiness || body.status || "updated")}. Refreshing...`;
    setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Owner/contact enrichment failed.";
  }
}

